import axios from "axios";
import { useEffect, useState, useRef } from "react";
import { PageHeader } from "../components/Base/PageHeader";
import { SkeletonServices } from "../components/CardSkeleton";
import AddServiceModal from "../components/Modals/AddServiceModal";
import NewEmptyState from "../components/NewEmptyState";
import Service from "../components/Service";
import ImagePlaceholder from "../assets/images/emptyService.svg";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { selectServices, SET_SERVICES } from "../features/pages/pages";
import { Button } from "../components/Base/Button";
import { Toast } from "../components/Base/toast";

const Staff = () => {
  const dispatch = useAppDispatch();
  const services = useAppSelector(selectServices);
  const [modalOpen, setModalOpen] = useState(false);
  const [fetch, setFetch] = useState(false);
  const [refreshPage, setRefreshPage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const getAllServices = async () => {
    try {
      !services.length && setFetch(true);
      const response = await axios.get(`/api/business/service`);
      dispatch(SET_SERVICES(response.data.data));
    } catch (e) {
      console.error(e);
    } finally {
      setFetch(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(
        `/api/business/service/import/csv/sample`,
        {
          responseType: "blob",
        }
      );

      const now = new Date();
      const formattedDateTime = now
        .toISOString()
        .replace(/T/, "_")
        .replace(/:/g, "-")
        .replace(/\..+/, "");

      const filename = `service_sample_${formattedDateTime}.csv`;

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Error downloading sample CSV:", error);
      Toast("Failed to download sample CSV", "error");
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `/api/business/service/import/csv`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Upload successful:", response.data);
      Toast("Upload success", "success");
      setRefreshPage((prev) => !prev);
    } catch (error) {
      console.error("Error uploading file:", error);
      Toast("Upload failed", "error");
    } finally {
      event.target.value = "";
    }
  };

  useEffect(() => {
    getAllServices();
  }, [refreshPage]);
  return (
    <div className="h-screen overflow-x-hidden">
      <PageHeader
        text="Services"
        rightElement={
          <div className="flex gap-2 items-center">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              accept=".csv"
              className="hidden"
            />

            <div className="flex shadow-sm rounded-md border border-gray-300 overflow-hidden">
              <button
                onClick={handleImportClick}
                className="bg-white text-gray-800 px-4 py-2 text-sm hover:bg-gray-100"
              >
                Import
              </button>
              <button
                className="bg-white text-gray-800 px-2 py-2 border-l border-gray-300 hover:bg-gray-100"
                onClick={handleDownload}
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>

            <Button onClick={() => setModalOpen(true)}>Create Service</Button>
          </div>
        }
      />
      {!fetch ? (
        services.length ? (
          <div className="grid grid-cols-4 gap-5 my-5">
            {services.map((service) => (
              <Service
                service={service}
                key={service.id}
                refresh={() => setRefreshPage((previous) => !previous)}
              />
            ))}
          </div>
        ) : (
          <NewEmptyState
            text=" You dont have any  services"
            buttonText="Create One"
            handleClick={() => {
              setModalOpen(true);
            }}
            image={ImagePlaceholder}
          />
        )
      ) : (
        <div className="grid grid-cols-4 gap-5 my-5">
          {[...Array(14)].map((_, i) => {
            return <SkeletonServices key={i} />;
          })}
        </div>
      )}

      {modalOpen && (
        <AddServiceModal
          modalOpen={modalOpen}
          closeModal={() => {
            setRefreshPage((previous) => !previous);
            setModalOpen(!modalOpen);
          }}
        />
      )}
    </div>
  );
};

export default Staff;
